import React, { Component } from 'react'
import PropTypes from 'prop-types'
import './styles.css'
import Icon from 'components/icons'
import LoadingSpinner from 'components/LoadingSpinner'
import { defineMessages, FormattedMessage } from 'react-intl'
import { MS_IN_SEC } from 'constants/dateTime'
import colors from '../../styles/colors.css'
import cn from 'classnames'
import { isVisManager } from 'utils/visManager'
import { VISIT_SUSPENDED_STATUS } from 'constants/visitStatuses'
import { createVideoService } from 'services'
import { ConnectionType } from 'constants/videoService'

const messages = defineMessages({
  visitNotStarted: 'Visit not started, please try again',
  userDisabled: 'Visit Muted',
  removedFromStation: 'Removed from station',
  loading: 'Loading',
  restricted: 'Restricted',
  visitSuspendedStatus: 'Visit is suspended'
})

const VISITOR = 'visitor'
const INMATE = 'inmate'

class MonitorPage extends Component {
  constructor(props) {
    super(props)
    this.state = {
      activeRoom: {},
      joinInitialized: false,
      muted: props.isVideoWall,
      visitorControlsVisible: false,
      inmateControlsVisible: false,
      visitorMuted: false,
      inmateMuted: false
    }
    this.inmateMedia = React.createRef()
    this.visitorMedia = React.createRef()
    this.onRoomConnectionSuccess = this.onRoomConnectionSuccess.bind(this)
    this.onRoomConnectionFailure = this.onRoomConnectionFailure.bind(this)
    this.joinVisit = this.joinVisit.bind(this)
    this.toggleAudio = this.toggleAudio.bind(this)
    this.showVisitControls = this.showVisitControls.bind(this)
    this.hideVisitControls = this.hideVisitControls.bind(this)
    this.toggleVisitControls = this.toggleVisitControls.bind(this)
  }

  componentDidMount() {
    if (this.props.isVideoWall) {
      this.joinVisit()
    }
  }

  async componentDidUpdate(prevProps) {
    if (!prevProps.webrtcToken && this.props.webrtcToken) {
      this.videoService = await this.setupVideoService()
      this.videoService.connect().then(this.onRoomConnectionSuccess, this.onRoomConnectionFailure)
    }
    if (!!this.props.monitorJoinError && this.props.isVideoWall) {
      this.timerId = setInterval(this.props.joinMonitor, 15 * MS_IN_SEC)
    }
    if (!!prevProps.monitorJoinError && !this.props.monitorJoinError) {
      clearInterval(this.timerId)
    }
  }

  async setupVideoService() {
    const videoService = await createVideoService(this.props.webrtcToken.provider, {
      webrtcToken: this.props.webrtcToken,
      roomName: this.props.roomName,
      connectionType: ConnectionType.MONITOR,
      videoWall: this.props.isVideoWall
    })
    return videoService
  }

  joinVisit() {
    this.setState({ joinInitialized: true })
    this.props.joinMonitor()
  }

  onRoomConnectionSuccess(room) {
    console.log('Connected to Room "%s"', room.name)
    this.setState({activeRoom: room})
    this.videoService.configureRemoteParticipants([
      { userId: this.props.inmateId.toString(), container: this.inmateMedia.current },
      { userId: this.props.visitorId.toString(), container:  this.visitorMedia.current }
    ])
  }

  onRoomConnectionFailure(err) {
    console.error('Failed to join Twilio room with error ', err)
  }

  toggleAudio() {
    if (this.state.activeRoom) {
      const muted = this.state.muted
      this.setState({ muted: !muted })
      this.videoService.setVolume(muted ? 100 : 0)
    }
  }

  getVideoControls(userType) {
    const {
      isVideoWall,
      inmateId,
      visitorId
    } = this.props
    const {
      activeRoom,
    } = this.state

    if (!isVideoWall && activeRoom) {
      const isMuted = this.state[`${userType}Muted`]
      const muteButtonClickHandler = (e) => {
        this.setState({ [`${userType}Muted`]: !isMuted })
        this.videoService.setVolume(
          isMuted ? 100 : 0,
          userType === INMATE ? inmateId.toString() : visitorId.toString()
        )
        e.stopPropagation()
      }
      const controlsAreVisible = this.state[`${userType}ControlsVisible`]
      return (
        <div styleName={cn('visitControls', {showControls: controlsAreVisible, hideControls: !controlsAreVisible})}>
          <button styleName={cn('unmuteButton', { soundOn: !isMuted })} onClick={muteButtonClickHandler}>
            <Icon name={isMuted ? 'vol-off' : 'vol-max'} fill={isMuted ? colors.shade0 : colors.shade5} width={30} />
          </button>
        </div>
      )
    } else {
      return null
    }
  }

  showVisitControls(event) {
    const userType = event.currentTarget.dataset.userType
    this.setState({ [`${userType}ControlsVisible`]: true })
  }

  hideVisitControls(event) {
    const userType = event.currentTarget.dataset.userType
    this.setState({ [`${userType}ControlsVisible`]: false })
  }

  toggleVisitControls(event) {
    const userType = event.currentTarget.dataset.userType
    const isVisible = this.state[`${userType}ControlsVisible`]
    if (isVisible) {
      this.hideVisitControls(event)
    } else {
      this.showVisitControls(event)
    }
  }

  render() {
    const {
      webrtcToken,
      monitorJoinError,
      userTracks,
      inmateId,
      visitorId,
      isVideoWall
    } = this.props
    const {
      muted,
      activeRoom
    } = this.state

    const inmateTracks = userTracks.find(t => t.userId === inmateId)
    const visitorTracks = userTracks.find(t => t.userId === visitorId)

    return (
      <div styleName='monitorContainer'>
        { !this.state.joinInitialized && (
          <Icon onClick={this.joinVisit} name='play' />
        )}

        { this.state.joinInitialized && !!webrtcToken && (
          <div styleName='videoBoxes'>
            <div styleName='videoBox inmate' data-user-type={INMATE}
                 onClick={this.toggleVisitControls} onMouseOver={this.showVisitControls} onMouseLeave={this.hideVisitControls}>
              <div styleName='videoWrapper' id='inmate_media' ref={this.inmateMedia} />
              { inmateTracks && inmateTracks.videoDisabled && inmateTracks.videoDisabledReason === 'user_action' && (
                <div styleName='notification'>
                  <Icon name='no-record' width={36} />
                  <FormattedMessage {...messages.userDisabled} />
                </div>
              )}
              { inmateTracks && inmateTracks.videoDisabled && ['power_action', 'nfc_action'].includes(inmateTracks.videoDisabledReason) && (
                <div styleName='notification'>
                  <Icon name='no-record' width={36} />
                  <FormattedMessage {...messages.removedFromStation} />
                </div>
              )}
              { this.getVideoControls(INMATE) }
            </div>
            <div styleName='videoBox visitor' data-user-type={VISITOR}
                 onClick={this.toggleVisitControls} onMouseOver={this.showVisitControls} onMouseLeave={this.hideVisitControls}>
              <div styleName='videoWrapper' id='visitor_media' ref={this.visitorMedia} />
              { visitorTracks && visitorTracks.videoDisabled && visitorTracks.videoDisabledReason === 'user_action' && (
                <div styleName='notification'>
                  <Icon name='cam-off' width={40} />
                  <FormattedMessage {...messages.userDisabled} />
                </div>
              )}
              { visitorTracks && visitorTracks.videoDisabled && ['power_action', 'nfc_action'].includes(visitorTracks.videoDisabledReason) && (
                <div styleName='notification'>
                  <Icon name='cam-off' width={40} />
                  <FormattedMessage {...messages.removedFromStation} />
                </div>
              )}
              { this.getVideoControls(VISITOR) }
            </div>
          </div>
        )}

        { isVisManager() && this.props.visitStatus === VISIT_SUSPENDED_STATUS && (
          <div styleName='notification'>
            <FormattedMessage {...messages.visitSuspendedStatus} />
          </div>
        )}

        { this.state.joinInitialized && monitorJoinError === 'not_found' && (
          <div styleName='error'>
            { !this.props.isVideoWall && (
              <FormattedMessage {...messages.visitNotStarted} />
            )}
            { this.props.isVideoWall && (
              <div styleName='loader'>
                <FormattedMessage {...messages.loading} />
                <LoadingSpinner color='#212529' size />
              </div>
            )}
          </div>
        )}

        { monitorJoinError === 'forbidden' && (
          <div styleName='error'>
            <div styleName='forbidden'>
              <Icon name='locked' width={30} />
              <FormattedMessage {...messages.restricted} />
            </div>
          </div>
        )}

        { isVideoWall && activeRoom && isVisManager() && (
          <div styleName='unmuteButtonWrapper'>
            <button styleName={cn('unmuteButton', { soundOn: !muted })} onClick={this.toggleAudio}>
              <Icon name={muted ? 'vol-off' : 'vol-max'} fill={muted ? colors.shade0 : colors.shade5} width={50} />
            </button>
          </div>
        )}

      </div>
    )
  }
}

MonitorPage.propTypes = {
  webrtcToken: PropTypes.object,
  roomName: PropTypes.string,
  toggleMicrophone: PropTypes.func,
  toggleCamera: PropTypes.func
}

export default MonitorPage
